/**
 * MaintenanceContext - Global maintenance mode state
 * When enabled, non-admin users can only access login and dashboard
 * Uses a public JSON file for cross-browser/cross-user state
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../api/client';

const MaintenanceContext = createContext({
  maintenanceMode: false,
  maintenanceMessage: '',
  setMaintenanceMode: () => {},
  checkMaintenanceStatus: () => {},
});

// Path to the maintenance status file (served from public folder)
const MAINTENANCE_FILE = '/maintenance-status.json';

export function MaintenanceProvider({ children }) {
  const [maintenanceMode, setMaintenanceModeState] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Check maintenance status from public JSON file
  const checkMaintenanceStatus = useCallback(async () => {
    try {
      // Fetch the public maintenance status file with cache busting
      const res = await fetch(`${MAINTENANCE_FILE}?t=${Date.now()}`);
      if (res.ok) {
        const data = await res.json();
        setMaintenanceModeState(data?.enabled || false);
        setMaintenanceMessage(data?.message || '');
      }
    } catch (e) {
      // If file doesn't exist or can't be read, assume no maintenance
      console.log('Maintenance status file not found, defaulting to disabled');
      setMaintenanceModeState(false);
    }
    setLoading(false);
  }, []);

  // Toggle maintenance mode - saves to server which writes the JSON file
  const setMaintenanceMode = useCallback(async (enabled, message = '') => {
    try {
      // Try to save to server API which will write the JSON file
      await api.post('/status/maintenance', { enabled, message });
      setMaintenanceModeState(enabled);
      setMaintenanceMessage(message);
    } catch (e) {
      console.error('Failed to set maintenance mode via API:', e);
      // Show error to admin
      throw e;
    }
  }, []);

  useEffect(() => {
    checkMaintenanceStatus();
    // Poll for changes every 10 seconds
    const interval = setInterval(checkMaintenanceStatus, 10000);
    return () => clearInterval(interval);
  }, [checkMaintenanceStatus]);

  return (
    <MaintenanceContext.Provider value={{ 
      maintenanceMode, 
      maintenanceMessage,
      setMaintenanceMode, 
      checkMaintenanceStatus,
      loading,
    }}>
      {children}
    </MaintenanceContext.Provider>
  );
}

export function useMaintenance() {
  return useContext(MaintenanceContext);
}

export default MaintenanceContext;
